<template>
<div>
  <div class="px-4 py-6 sm:px-6">
    <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">{{ randomGreeting }}, {{ user.Name.split(' ')[0] }}</h2>
  </div>

  <div v-if="user && (!progressComplete || (progressComplete && user.Published && !user.HasPassedTtiReview) || progressComplete && !user.Published)" class="bg-blue-600">
    <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6">
      <div class="flex flex-col items-start justify-between flex-wrap sm:flex-row sm:items-">
        <div class="flex-1 flex items-center">
          <span class="flex self-start mt-px">
            <!-- Heroicon name: outline/speakerphone -->
            <svg class="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </span>
          <div class="ml-3 space-y-1">
            <template v-if="!progressComplete">
              <p class="text-lg font-medium text-white lg:text-xl">
                Complete your profile.
              </p>
              <p class="text-blue-100">
                Finish adding details to your profile.
              </p>
              <ul>
                <li v-for="p in progress" :key="p.title" class="flex space-x-1 text-sm text-blue-100">
                  <check-icon v-if="p.complete" class="w-5 h-5 flex-shrink-0"></check-icon>
                  <span v-else class="inline-block w-5 h-5 text-center text-blue-300 font-bold">–</span>
                  <span>{{ p.title }}</span>
                </li>
              </ul>
            </template>
            <template v-else-if="progressComplete && user.Published && !user.HasPassedTtiReview">
              <p class="text-lg font-medium text-white lg:text-xl">
                Your profile is being reviewed.
              </p>
              <p class="text-blue-100 text-sm">
                Your profile has been submitted for review. One of our assessors will check your profile meets <router-link to="/articles/our-approach-to-quality" class="underline">our approach to quality</router-link>, after which your profile will be automatically published, or we will let you know what changes need to be made.<br />
                You can continue to make changes or, if you wish, you can <button @click="publishProfile(false)" class="underline">un-publish</button> to have it reviewed at a later date.
              </p>
            </template>
            <template v-else-if="progressComplete && !user.Published">
              <p class="text-lg font-medium text-white lg:text-xl">
                Your profile is in draft mode.
              </p>
              <p class="text-blue-100 text-sm">
                You have completed the key areas of your profile, but no-one can see it yet.
                <template v-if="user.HasPassedTtiReview">
                  When you are ready, hit Publish to make your profile public.
                </template>
                <template v-else>
                  When you are ready, hit Publish to submit your profile for mandatory review before going public.
                </template>
              </p>
            </template>

<!-- 
            <p class="text-lg font-medium text-white lg:text-xl">
              Your profile is in draft mode.
            </p>

            <p v-if="!progressComplete" class="text-blue-100">
              Finish adding details to your profile ready for making it public.
            </p>
            <p v-else class="flex space-x-1 text-blue-100 text-sm">
              <check-icon class="-mt-0.5 w-6 h-6 flex-shrink-0"></check-icon>
              <span>You have completed the key areas of your profile. When you are ready, hit Publish to make your profile public.</span>
            </p>

            <ul v-if="!progressComplete">
              <li v-for="p in progress" :key="p.title" class="flex space-x-1 text-sm text-blue-100">
                <check-icon v-if="p.complete" class="w-5 h-5 flex-shrink-0"></check-icon>
                <span v-else class="inline-block w-5 h-5 text-center text-blue-300 font-bold">–</span>
                <span>{{ p.title }}</span>
              </li>
            </ul> -->
          </div>
        </div>
        <div v-if="progressComplete && !user.Published" class="order-2 mt-4 ml-9 flex-shrink-0 sm:order-2 sm:mt-1 sm:w-auto sm:ml-5">
          <button type="button" @click="publishProfile(true)" class="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-blue-600 bg-white hover:bg-blue-50">
            Publish Profile
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-gray-200 overflow-hidden border-t border-b divide-y divide-gray-200 sm:divide-y-0 sm:grid sm:grid-cols-2 sm:gap-px">
    <div 
      v-for="action in actions" 
      :key="action.title" 
      :class="['relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500']"
    >
      <div>
        <span :class="[action.iconBackground, action.iconForeground, 'rounded-lg inline-flex p-3 ring-4 ring-white']">
          <component :is="action.icon" class="h-6 w-6" aria-hidden="true" />
        </span>
      </div>
      <div class="mt-6">
        <h3 class="text-lg font-medium">
          <button type="button" @click="action.click" class="focus:outline-none">
            <!-- Extend touch target to entire panel -->
            <span class="absolute inset-0" aria-hidden="true" />
            {{ action.title }}
          </button>
        </h3>
        <p class="mt-2 text-sm text-gray-500">
          {{ action.subtext }}
        </p>
      </div>
      <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400" aria-hidden="true">
        <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
        </svg>
      </span>
    </div>
  </div>
</div>
</template>

<script>
import { ShieldCheckIcon, CreditCardIcon, InboxIcon, ShareIcon, CheckIcon, } from '@vue-hero-icons/solid'

export default {
  components: {
    CheckIcon,
  },
  computed: {
    actions() {
      return [{
        title: 'DBS Check',
        subtext: 'Reassure your prospective clients that you are a trustworthy choice.',
        click: () => { this.$router.push({ name: 'EditTutor-DbsCheck' }) },
        icon: ShieldCheckIcon,
        iconForeground: 'text-blue-500',
        iconBackground: 'bg-blue-50',
      },
      {
        title: 'Online Payments',
        subtext: 'Receive your lesson fees and other payments via your personal, online payment page.',
        click: () => { this.$router.push({ name: 'EditTutor-OnlinePayments' }) },
        icon: CreditCardIcon,
        iconForeground: 'text-green-600',
        iconBackground: 'bg-green-50',
      },
      {
        title: 'Enquiries',
        subtext: 'Enquires you have received from prospective students visiting your profile.',
        click: () => { this.$router.push({ name: 'EditTutor-Enquiries' }) },
        icon: InboxIcon,
        iconForeground: 'text-purple-600',
        iconBackground: 'bg-purple-50',
      },
      {
        title: 'Share',
        subtext: 'Make it simple for people to find you by sharing a direct link to your profile.',
        click: () => { 
          navigator.share({
            title: `Check out my profile on The Tutor Index`,
            url: `https://www.thetutorindex.com/${this.publicProfileUrl}`
          }) 
        },
        icon: ShareIcon,
        iconForeground: 'text-yellow-700',
        iconBackground: 'bg-yellow-50',
      }]
    },
    randomGreeting() {
      const greetings = ['Hi', 'Hello', 'Bonjour', 'Salut', 'Hola', 'Nǐn hǎo', 'Ciao', 'Konnichiwa', 'Guten Tag', 'Hallo', 'Olá', 'Halløj', 'Hai', 'Hey', 'Hei', 'God dag']
      return greetings[Math.floor(Math.random() * greetings.length)]
    },
    user() {
      return this.$store.getters.user
    },
    progress() {
      return this.$store.getters.progress
      // return [
      //   { title: 'Add your name.', complete: !!this.user.Name },
      //   { title: 'Upload a profile photo.', complete: !!this.user.Photo },
      //   { title: 'Tell us where you reside.', complete: !!this.user.ResidenceCountry },
      //   { title: 'Introduce yourself in your bio.', complete: !!this.user.Bio },
      //   { title: 'Describe your approach to teaching.', complete: !!this.user.ApproachToTeaching },
      //   { title: 'Explain your previous teaching experience.', complete: !!this.user.TeachingExperience },
      //   { title: 'List the subjects you teach.', complete: !!this.user.SubjectsTaught.length > 0 },
      //   { title: 'Specify where you teach.', complete: this.user.Locations.length > 0 || this.user.TeachesOnline },
      //   { title: 'Provide information about the rate you charge.', complete: !!this.user.RateFrom },
      //   { title: 'Send and receive a test enquiry email.', complete: this.user.HasViewedTestEnquiryEmail },
      //   // { title: 'Write an article.', complete: this.user.Articles.filter(x => x.Status === 'PUBLISHED').length > 0 },
      //   { title: 'Complete an informal interview.', complete: this.user.HasPassedInterview },
      // ]
    },
    progressComplete() {
      return this.progress.every(x => x.complete)
    }
  },
  methods: {
    publishProfile(publish) {
      this.$store.dispatch('updateUser', { Published: publish }).then(() => {
        this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
      })
    }
  }
}
</script>

<style>

</style>